import * as React from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'

import { noop } from '@livechat/data-utils'
import { extendURLByQueryParams, getOrigin } from '@livechat/url-utils'

import { useUser } from './useUser'

const TemplateIdContext = React.createContext<{
	withTemplateId: (urlOrHref: string) => string
	setUrlQueryTemplateId: (templateId: string) => void
	templateId: string
}>({
	withTemplateId: (urlOrHref: string) => urlOrHref,
	setUrlQueryTemplateId: noop,
	templateId: '',
})

const useTemplateId = () => React.useContext(TemplateIdContext)

type ProviderProps = {
	children: React.ReactNode
}

export const TemplateIdProvider = ({ children }: ProviderProps) => {
	const router = useRouter()
	const user = useUser()
	const dispatch = useDispatch()
	const searchParams = useSearchParams()
	const templateId = searchParams.get('tid') ?? user?.organizationId

	React.useEffect(() => {
		dispatch({ type: 'state/reset' })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateId])

	const setUrlQueryTemplateId = React.useCallback(
		(newTemplateId: string) => {
			router.push({ query: { ...router.query, tid: newTemplateId } }, undefined, {
				shallow: true,
			})
		},
		[router],
	)

	const withTemplateId = React.useCallback(
		(urlOrHref: string) => {
			if (!searchParams.has('tid')) {
				return urlOrHref
			}

			if (getOrigin(urlOrHref) !== null) {
				return extendURLByQueryParams(urlOrHref, { tid: templateId })
			}

			const paramGlue = urlOrHref.includes('?') ? '&' : '?'
			return `${urlOrHref}${paramGlue}tid=${templateId}`
		},
		[templateId, searchParams],
	)

	const value = { withTemplateId, templateId, setUrlQueryTemplateId }

	return <TemplateIdContext.Provider value={value}>{children}</TemplateIdContext.Provider>
}

export default useTemplateId
